<template>
  <v-container fluid>
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Herd Sort'"
      v-bind:icon="'sort'"
      v-bind:actionLink="'Manage Queries'"
      v-bind:actionLinkHidden="isLoading || !isUserQueries"
      @action-link-click="actionLinkClicked"
    />

    <!-- No Herds Assigned -->
    <template v-if="!isLoading && !isUserHerd">
      <app-alert v-bind:alertType="'error'" v-bind:message="'You have no herds assigned'" />
    </template>

    <!-- No Queries Exist -->
    <template v-if="!isLoading && !isUserQueries" >
      <v-alert color="info" type="info" border="top" colored-border prominent>
        <v-row justify="center">
          <v-col class="grow">          
            <v-row justify="center">
              <span class="title">You have no queries defined</span>
            </v-row>
            <v-row justify="center">
              <span class="subtitle-1">Go to <i>Query Manager</i> to get started</span>
            </v-row>
          </v-col>
          <v-col class="shrink">
            <v-btn to="/herdSortManager" dark>Query Manager</v-btn>
          </v-col>
        </v-row>
      </v-alert>
    </template>

    <!-- Good To Go -->
    <template v-if="!isLoading && isUserHerd && isUserQueries">
      <v-row no-gutters class="mx-2">

        <!-- Queries -->
        <v-col
          cols="12"
          xs="12"
          sm="12"
          md="5"
          >
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-header :color="appColor_Medium" class="elevation-4 py-0">
                <span class="title font-weight-regular white--text">Your Queries</span>
                <template v-slot:actions>
                  <v-icon large color="white">arrow_drop_down</v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list class="pt-2 pb-0">
                  <v-card
                    v-for="query in pageQueries"
                    :key="query.id"
                    :color="query.active ? query.colourCode + ' lighten-2' : 'grey lighten-1'"
                    @click="activateQuery(query)"
                    class="mb-2"
                    >
                    <user-query
                      v-bind:scope="scope"
                      v-bind:query="query"
                      v-bind:activeQuery="activeQuery"
                      v-bind:colourCode="query.colourCode"
                      v-bind:chipPrimaryTrait="query.userQueryDef.userTraits[0].description"
                      v-bind:isEditQueryClicked="activeQuery.id === query.id && isEditQueryClicked"
                      @change="refreshAnimals(true)"
                      @cancel="isEditQueryClicked = false"
                      >
                    </user-query>
                  </v-card>

                  <!-- Pagination -->  
                  <template v-if="pagingDisabled">
                    <v-row justify="center">
                      <v-pagination
                        v-model="page"
                        light :length="pageLength"
                      />
                    </v-row>
                  </template>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <!-- Search Results -->
        <v-col
          cols="12"
          xs="12"
          sm="12"
          md="7"
          >

          <v-data-table
            :headers="headers"
            :items="activeResult.qualifiedAnimals"
            :loading="activeResult.isRefreshing"
            :no-data-text="noDataText"
            :class="searchResultsClass"
            :search="search"
            :custom-filter="customFilter"
            mobile-breakpoint="500"
            >

            <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate/>

            <!-- Header Toolbar -->
            <template v-slot:top>
              <v-toolbar dense dark :color="appColor_Medium">
                <v-toolbar-title>{{ activeQueryTitle }}</v-toolbar-title>
                <v-spacer />

              <!-- Edit Query Button -->
              <v-btn
                outlined
                :disabled="activeResult.isRefreshing"
                :small="isSmallDevice"
                @click="isEditQueryClicked = true"
                class="mr-2"
                >
                Edit Query
              </v-btn>

                <!-- Export -->
                <v-menu
                  v-if="activeResult.pctErrMessage !== null"
                  bottom
                  offset-y
                  >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      :small="isSmallDevice"
                      outlined
                      v-on="on"
                      >
                      <v-icon :small="isSmallDevice" color="red">
                        priority_high
                      </v-icon>
                    </v-btn>
                  </template>
                  <v-list
                    two-line
                    class="popup-list pb-0"
                    style="min-width:150px"
                    >
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>An error occurred getting EBV percentile limits.</v-list-item-title>
                        <router-link to="/herdSort">
                          <span @click="refreshAnimals(true)">Click here to try again.</span>
                        </router-link>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-btn
                  :small="isSmallDevice"
                  :disabled="activeResult.isRefreshing"
                  outlined
                  @click="exportDialog=true"
                  >
                  <v-icon :small="isSmallDevice">file_download</v-icon>
                </v-btn>
              </v-toolbar>

              <!-- Filter Toolbar -->
              <v-toolbar dense flat class="mt-2">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  clearable
                />
              </v-toolbar>
            </template>

            <!-- Bull Rank Button Menu -->
            <template v-slot:item.animal.rank="{ item }">
              <animal-rank-menu
                v-bind:rank="item.animal.rank"
                v-bind:rankColor="getRankColor(item.sequencedMatches)"
                v-bind:displayValues="item.animalDisplayValues"
                v-bind:criteriaTraits="item.animalCriteriaTraits"
                v-bind:criterionDescriptions="activeResult.criterionDescriptions"
              />
            </template>

            <!-- Markers -->
            <template v-slot:item.animal.favorites="{ item }">
              <v-row>
                <v-flex sm6>
                  <span
                    class="hover-pointer"
                    @click="toggleFavorite(item.animal)"
                    >
                    <v-icon
                      v-if="item.animal.favorites === null"
                      color="grey lighten-1"
                    >star_border</v-icon>
                    <v-icon
                      v-else
                      color="yellow darken-2"
                    >star</v-icon>
                  </span>
                </v-flex>
                <v-flex sm6>
                  <span
                    class="hover-pointer"
                    @click="toggleFlag(item.animal)"
                    >
                    <v-icon
                      v-if="!item.animal.isFlagged"
                      color="grey lighten-1"
                      class="ml-1"
                    >flag</v-icon>
                    <v-icon
                      v-else
                      color="red darken-4"
                      class="ml-1"
                    >flag</v-icon>
                  </span>
                </v-flex>
              </v-row>
            </template>

          </v-data-table>
        </v-col>

      </v-row>

      <!-- Export Dialog -->
      <v-dialog v-model="exportDialog" persistent max-width="250px">
        <app-export v-model="isExporting" v-bind:title="'Export To File'" @ok="downloadExport" @cancel="exportDialog = false" />
      </v-dialog>

      <!-- Error Dialog -->
      <v-dialog
        v-model="errorDialog"
        max-width="400px"
        >
        <app-error
          :error-title="errorTitle"
          :error-text="errorText"
          @close="errorDialog = false"
        />
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppAlert from '@/components/AppAlert'
import AppPageTitle from '@/components/AppPageTitle'
import UserQuery from '@/components/UserQuery'
import AnimalRankMenu from '@/components/AnimalRankMenu'
import AppExport from '@/components/AppExport'
import AppError from '@/components/AppError'
import { mapGetters } from 'vuex'
import { AppColor, InputTypeId, CriterionTypeId } from '@/config.js'

export default {
  name: 'HerdSort',
  components: {
    AppAlert,
    AppPageTitle,
    UserQuery,
    AnimalRankMenu,
    AppExport,
    AppError
  },
  data () {
    return {

      // app colours
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Error: AppColor.Error,
      
      // input types
      inputTypeId_RangeSlider: InputTypeId.RangeSlider,
      inputTypeId_MultiSelect: InputTypeId.MultiSelect,

      // criterion types
      criterionTypeId_Filter: CriterionTypeId.Filter,
      criterionTypeId_Trait: CriterionTypeId.Trait,
      criterionTypeId_OptionalFilter: CriterionTypeId.Filter,

      pctErrMessage: null,
      scope: 'herdtrax',
      isLoading: true,
      isSaving: false,
      isExporting: false,
      isUserHerd: false,
      isUserQueries: false,
      detailDialog: false,
      detailSettings: { isPrioritize: false, panel: 0 },
      exportDialog: false,
      search: '',
      panel: 0,
      page: 1,
      pageSize: 4,
      optionalFilters: [],
      traitsContainer: {},
      queries: [],
      results: [],
      headers: [
        {
          text: 'Rank',
          value: 'animal.rank',
          align: 'center',
          width: '15%'
        },
        {
          text: 'Visual Id',
          value: 'animal.visualId',
          align: 'left',
          width: '25%'
        },
        {
          text: 'Year',
          value: 'animal.yob',
          align: 'left',
          width: '15%'
        },
        {
          text: 'BWT',
          value: 'animal.birthWt',
          align: 'left',
          width: '15%'
        },
        {
          text: 'Cycle',
          value: 'animal.estrusCycle',
          align: 'left',
          width: '15%'
        },
        {
          text: 'Markers',
          value: 'animal.favorites',
          align: 'left',
          width: '15%'
        }
      ],

      // query edit
      isEditQueryClicked: false,

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null,
      isError: false
    }
  },
  computed: {
    ...mapGetters(['profile']),
    noDataText () {
      return this.activeResult.isRefreshing
        ? 'Searching...'
        : 'No animals found'
    },
    pageQueries () {
      const beginId = ((this.page - 1) * this.pageSize)
      const endId = beginId + this.pageSize + 1
      return this.queries.filter(q => { return q.index > beginId && q.index < endId })
    },
    searchResultsHeader () {
      return this.activeQuery !== null
        ? 'Search Results for: ' + this.activeQuery.title
        : 'No Queries Selected'
    },
    pageLength () {
      return this.queries.length > this.pageSize
        ? Math.ceil(this.queries.length / this.pageSize)
        : 0
    },
    pagingDisabled () {
      return this.queries.length > this.pageSize
    },
    activeQuery () {
      if (this.queries === null) return null

      const activeQueries = this.queries.filter(q => { return q.active })
      return activeQueries.length > 0
        ? activeQueries[0] : null
    },
    activeQueryTitle () {
      const activeQuery = this.activeQuery
      return activeQuery !== null
        ? activeQuery.title
        : ''
    },
    activeResult () {
      if (this.activeQuery === null) return { qualifiedAnimals: [] }

      const queryId = this.activeQuery.id
      const results = this.results.filter(res => { return res.queryId === queryId })
      return results.length > 0
        ? results[0]
        : null
    },
    searchResultsClass () {
      return {
        'pa-0': true,
        'ml-2': !this.isSmallDevice,
        'mt-2': this.isSmallDevice
      }
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    }
  },
  methods: {
    loadData () {
      const self = this
      if (self.profile.id === undefined) self.$router.push('/')
      appService.getForHerdSort(self.profile.id).then(data => {
        if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.isUserHerd = true
          self.isUserQueries = data.queries.length > 0
          self.queries = data.queries

          // set the index for paging
          // and initialize the query result containers
          var results = []
          self.queries.forEach((q, idx) => {
            q.index = idx + 1
            results.push({
              queryId: q.id,
              qualifiedAnimals: [],
              criterionDescriptions: null,
              isRefreshing: false,
              pctErrMessage: null
            })
          })
          self.results = results
          self.isLoading = false
          self.refreshAnimals()
        }
      })
    },

    refreshAnimals (force = false) {
      if (this.activeQuery === null) return

      const self = this
      const query = self.activeQuery
      const activeResult = self.activeResult
      self.isError = false
      self.isEditQueryClicked = false

      if ((activeResult.qualifiedAnimals.length === 0 || force) && !activeResult.isRefreshing) {
        activeResult.isRefreshing = true
        activeResult.qualifiedAnimals = []
        appService.queryAnimals(self.profile.id, query.id, self.getSearchCriteria()).then(data => {
          if (data.errorSeverity > 0) {
            self.isError = true
            self.errorText = data.errorMessage
            self.errorDialog = true
          } else {
            var targetResults = self.results.filter(function (value) {
              return value.queryId === data.queryId
            })
            if (targetResults.length > 0) {
              targetResults[0].criterionDescriptions = data.criterionDescriptions
              targetResults[0].qualifiedAnimals = data.qualifiedAnimals
              targetResults[0].pctErrMessage = data.errorMessage
            }
          }
          activeResult.isRefreshing = false
        })
      }
    },

    getSearchCriteria () {
      var self = this
      var criteriaModels = []

      // assemble filter criteria
      var filters = self.activeQuery.userQueryDef.userFilters
      for (var key1 in filters) {
        criteriaModels.push({
          criterionId: filters[key1].filterId,
          criterionTypeId: self.criterionTypeId_Filter,
          columnName: filters[key1].columnName,
          dataTypeId: filters[key1].dataTypeId,
          minValue: filters[key1].value,
          maxValue: filters[key1].value
        })
      }

      /// assemble trait criteria
      var traits = self.activeQuery.userQueryDef.userTraits
      var seq = 1
      for (var key2 in traits) {
        criteriaModels.push({
          criterionId: traits[key2].traitId,
          criterionTypeId: self.criterionTypeId_Trait,
          trait: traits[key2].description,
          searchTypeId: traits[key2].searchTypeId,
          dataTypeId: traits[key2].dataTypeId,
          unit: traits[key2].group === 'EBV' ? 'EBV' : traits[key2].unitAbbreviation,
          minValue: traits[key2].inputTypeId === self.inputTypeId_RangeSlider
            ? traits[key2].minMaxValue[0].toString()
            : traits[key2].inputTypeId !== self.inputTypeId_MultiSelect
              ? traits[key2].value
              : traits[key2].multiSelectValues.join(),
          maxValue: traits[key2].inputTypeId === self.inputTypeId_RangeSlider
            ? traits[key2].minMaxValue[1].toString()
            : traits[key2].inputTypeId !== self.inputTypeId_MultiSelect
              ? traits[key2].value
              : traits[key2].multiSelectValues.join(),
          sequence: seq++
        })
      }

      // assemble optional filter criteria
      var optionals = self.activeQuery.userQueryDef.optionalFilters
      for (var key3 in optionals) {
        criteriaModels.push({
          criterionId: optionals[key3].filterId,
          criterionTypeId: self.criterionTypeId_OptionalFilter,
          trait: optionals[key3].description,
          dataTypeId: optionals[key3].dataTypeId,
          minValue: optionals[key3].value,
          maxValue: optionals[key3].value
        })
      }

      return criteriaModels
    },

    downloadExport ({ exportType }) {
      const self = this
      const query = self.activeQuery
      if (query === null) return

      self.isExporting = true
      const animalIds = self.activeResult.qualifiedAnimals.map(qa => parseInt(qa.animal.id))   
      const exportCriteria = {
        userId: self.profile.id,
        queryId: self.activeQuery.id,
        title: self.activeQuery.title,
        description: self.activeQuery.description,
        criteria: self.getSearchCriteria(),
        fileType: exportType,
        animalIds: animalIds
      }
      appService.getHerdSortExport(exportCriteria).then(() => {
        self.isExporting = false
        self.exportDialog = false
      })
    },

    toggleFavorite (animal) {
      if (animal.favorites === null) {
        animal.favorites = []
      } else {
        animal.favorites = null
      }
    },

    toggleFlag (animal) {
      animal.isFlagged = !animal.isFlagged
    },

    activateQuery (query) {
      const self = this
      if (self.activeQuery.id === query.id) return

      self.queries.forEach(q => { q.active = (q.id === query.id) })
      self.refreshAnimals()
    },

    getRankColor (matches) {
      const numCriteria = this.activeQuery.userQueryDef.userTraits.length
      // perfect
      if (matches === numCriteria) {
        return 'green'
      // average
      } else if (matches === numCriteria - 1) {
        return 'yellow'
      // poor
      } else if ((matches > 0) && (matches <= numCriteria - 2)) {
        return 'red'
      // not a match
      } else return 'grey'
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.animal.visualId.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    actionLinkClicked () {
      this.$router.push('/herdSortManager')
    }
  },
  created () {
    this.loadData()
  }
}
</script>
